import { useEffect } from 'react'

/**
 * useSeo — set per-page <title>, meta description, and canonical link for the
 * spider-web blade pages (each leg should rank on its own term).
 *
 * SPA-friendly: writes into <head> on mount and restores the previous values on
 * unmount, so navigating between blades doesn't leak stale tags. Guarded for
 * non-DOM environments (tests/SSR).
 */
export interface SeoOptions {
  title: string
  description?: string
  /** Absolute canonical URL. Defaults to the current location (path only). */
  canonical?: string
}

const SITE = 'https://beetlesense.ai'

function upsertMeta(name: string): { el: HTMLMetaElement; prev: string | null } {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute('name', name)
    document.head.appendChild(el)
  }
  return { el, prev: el.getAttribute('content') }
}

function upsertCanonical(): { el: HTMLLinkElement; prev: string | null; created: boolean } {
  let el = document.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  let created = false
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
    created = true
  }
  return { el, prev: el.getAttribute('href'), created }
}

export function useSeo({ title, description, canonical }: SeoOptions): void {
  useEffect(() => {
    if (typeof document === 'undefined') return
    // Empty title = disabled (lets callers keep hook order stable while opting out).
    if (!title) return

    const prevTitle = document.title
    document.title = title

    const desc = description ? upsertMeta('description') : null
    if (desc && description) desc.el.setAttribute('content', description)

    const href = canonical ?? `${SITE}${window.location.pathname}`
    const canon = upsertCanonical()
    canon.el.setAttribute('href', href)

    return () => {
      document.title = prevTitle
      if (desc) {
        if (desc.prev === null) desc.el.remove()
        else desc.el.setAttribute('content', desc.prev)
      }
      if (canon.created) canon.el.remove()
      else if (canon.prev !== null) canon.el.setAttribute('href', canon.prev)
    }
  }, [title, description, canonical])
}
